import { useTeamCatalog, withTeamCatalog } from '@context/TeamCatalog.context'
import React, { useState } from 'react'
import { ICallFlowStep } from '@dataTypes/clinic.type'
import { InputField, RichTextEditor, Select } from '@enamelai/design-system'
import { smsMessagePair } from '@utils/communicationFlow.util'
import StepPanelShell from './StepPanelContainer.component'

type RouteSmsStep = Extract<ICallFlowStep, { type: 'route_sms' }>

export type RouteSmsPanelProps = {
  step: RouteSmsStep
  branchLabel: string
  aiEnabled?: boolean
  onChange: (patch: Partial<RouteSmsStep>) => void
  onCancel: () => void
  onDone: () => void
}

const FALLBACK_OPTIONS = [
  { value: 'send_sms', label: 'Send SMS' },
  { value: 'ai_receptionist', label: 'AI receptionist' },
]

const RouteSmsPanel = ({
  step,
  branchLabel,
  aiEnabled = false,
  onChange,
  onCancel,
  onDone,
}: RouteSmsPanelProps): React.ReactElement => {
  const { teams } = useTeamCatalog()
  const [errors, setErrors] = useState<Record<string, string>>({})
  const fallbackOptions = FALLBACK_OPTIONS.filter(
    (option) => option.value !== 'ai_receptionist' || aiEnabled,
  )

  const save = (): void => {
    const next: Record<string, string> = {}
    if (!step.team?.trim()) next.team = 'Select a team.'
    if (
      step.timeoutMinutes == null ||
      Number.isNaN(Number(step.timeoutMinutes)) ||
      Number(step.timeoutMinutes) <= 0
    ) {
      next.timeoutMinutes = 'Enter how many minutes to wait.'
    }
    if (step.ifNoReply !== 'send_sms' && step.ifNoReply !== 'ai_receptionist') {
      next.ifNoReply = 'Select an action.'
    }
    const pair = smsMessagePair(step.messageHtml, step.message)
    if (step.ifNoReply === 'send_sms' && !pair.message) {
      next.message = 'Enter a message.'
    }
    setErrors(next)
    if (Object.keys(next).length > 0) return
    onChange({
      team: step.team?.trim(),
      timeoutMinutes: Number(step.timeoutMinutes),
      ifNoReply: step.ifNoReply,
      messageHtml: step.ifNoReply === 'send_sms' ? pair.messageHtml : undefined,
      message: step.ifNoReply === 'send_sms' ? pair.message : undefined,
    })
    onDone()
  }

  return (
    <StepPanelShell
      title="Route"
      branchLabel={branchLabel}
      onCancel={onCancel}
      onDone={save}
    >
      <Select
        label="Team"
        placeholder="Select team"
        options={teams.map((team) => ({ label: team, value: team }))}
        value={step.team || undefined}
        error={Boolean(errors.team)}
        helperText={errors.team}
        onChange={(value) => {
          setErrors((current) => ({ ...current, team: '' }))
          onChange({ team: String(value || '') || undefined })
        }}
        className="scp-route-sms-team-select"
      />
      <div className="scp-route-sms-grid-2">
        <InputField
          variant="number"
          label="Wait time"
          value={step.timeoutMinutes == null ? '' : String(step.timeoutMinutes)}
          error={Boolean(errors.timeoutMinutes)}
          helperText={errors.timeoutMinutes}
          trailingIcon={<span>minutes</span>}
          onChange={(event) => {
            setErrors((current) => ({ ...current, timeoutMinutes: '' }))
            const raw = event.target.value
            onChange({
              timeoutMinutes: raw === '' ? undefined : Number(raw),
            })
          }}
        />
        <Select
          label="If no answer"
          placeholder="Select action"
          options={fallbackOptions}
          value={step.ifNoReply || undefined}
          error={Boolean(errors.ifNoReply)}
          helperText={errors.ifNoReply}
          onChange={(value) => {
            const next = String(value || '') as RouteSmsStep['ifNoReply']
            setErrors((current) => ({ ...current, ifNoReply: '' }))
            onChange({
              ifNoReply: next || undefined,
              messageHtml: next === 'send_sms' ? step.messageHtml : undefined,
              message: next === 'send_sms' ? step.message : undefined,
            })
          }}
        />
      </div>

      {step.ifNoReply === 'send_sms' ? (
        <div className="scp-sms-message-field">
          <span className="ds-input-field__label">Enter greeting message</span>
          <RichTextEditor
            variant="sms-notes"
            value={step.messageHtml ?? step.message ?? ''}
            placeholder="Type your message here"
            bodyActions={[]}
            footerActions={[]}
            aiEnabled={false}
            onChange={(next) => {
              setErrors((current) => ({ ...current, message: '' }))
              const pair = smsMessagePair(next, undefined)
              onChange({
                messageHtml: pair.messageHtml,
                message: pair.message || undefined,
              })
            }}
          />
          {errors.message ? (
            <p className="scp-step-panel-muted">{errors.message}</p>
          ) : null}
        </div>
      ) : null}
    </StepPanelShell>
  )
}

export default withTeamCatalog(RouteSmsPanel)
